import { useCallback, useEffect, useMemo, useState } from 'react';
/**
 * Simple useFetch hook that encapsulates any API call.
 *
 * @example
 * const { data, error, loading, refetch } = useFetch({
 *   fetcher: () => http.get('/path/to/be')
 * })
 */
export default function useFetch({
  fetcher,
  skip
}) {
  const [state, setState] = useState({
    called: false,
    data: undefined,
    error: undefined,
    loading: !skip
  });
  const resetState = useCallback(() => {
    setState(prev => {
      const newLoading = !skip;
      if (prev.data === undefined && prev.error === undefined && prev.loading === newLoading) {
        return prev;
      }
      return {
        called: false,
        data: undefined,
        error: undefined,
        loading: newLoading
      };
    });
  }, [skip]);
  const fetchRequest = useCallback(({
    forceFetch,
    cachedData
  } = {}) => {
    if (!cachedData) {
      resetState();
    }
    if (skip && !forceFetch) {
      return;
    }
    fetcher().then(response => {
      setState({
        called: true,
        data: response,
        error: undefined,
        loading: false
      });
    }).catch(error => {
      setState({
        called: true,
        data: undefined,
        error,
        loading: false
      });
    });
  }, [fetcher, resetState, skip]);
  const refetch = useCallback(cachedData => {
    if (cachedData) {
      setState(prev => Object.assign({}, prev, {
        data: cachedData
      }));
    }
    fetchRequest({
      forceFetch: true,
      cachedData
    });
  }, [fetchRequest]);
  useEffect(() => {
    fetchRequest();
  }, [fetchRequest]);
  return useMemo(() => Object.assign({}, state, {
    refetch
  }), [refetch, state]);
}